import { useEffect } from 'react';
import { useTerminalStore } from '../store/useTerminalStore';
import { gatewayApi } from '../lib/api';

/**
 * Periodically refreshes `equityHistory` from `/api/portfolio/equity` so the
 * chart and badge stay current after the one-shot bootstrap fetch.
 *
 * Codex PR #103 review (2026-05-14): without this hook the chart would freeze
 * for the session lifetime once `useBootstrap` writes the initial 30-day
 * series. This polls every 60 seconds (production-typical equity update
 * cadence). Replace with a portfolio-level WS subscription when available
 * (post-S8 follow-up).
 *
 * Errors are swallowed — keep showing the most recent successful snapshot
 * rather than crashing on transient network blips. Pattern mirrors
 * `useBarPolling`.
 */
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function useEquityPolling(intervalMs: number = 60_000, enabled: boolean = true): void {
  useEffect(() => {
    if (!enabled) return;
    async function poll(): Promise<void> {
      if (document.hidden) return;
      try {
        const now = Date.now();
        const records = await gatewayApi.portfolioEquity(now - THIRTY_DAYS_MS, now);
        useTerminalStore.getState().setEquityHistory(records);
      } catch {
        // Swallow — polling is best-effort. Prior snapshot stays in the store.
      }
    }

    const id = setInterval(poll, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs]);
}
